import PartiesIcon from "@mui/icons-material/People";
import { Box, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCase, useCaseSubmit } from "../../context/CaseContext";
import { useTheme } from "../../context/ThemeContext";
import { CostApportionment, PartyDescriptors } from "../../shared/CaseModel";
import { NOTHING_LOCKED } from "../../shared/costChanges";
import { Budget } from "../../shared/CostModel";
import CenteredMessageBox from "../general/CenteredMessageBox";
import { Tabs } from "../general/Tabs";
import { GuideHeader } from "../guide/GuideHeader.tsx";
import { TonalBadge } from "../general/TonalBadge";
import { CostApportionmentsOfParty } from "./CostApportionmentsOfParty";

export interface CostApportionmentCallbacks {
  /** Saves the party's apportionment, and its costs where they change with it. */
  update(
    partyId: string,
    newValue: CostApportionment,
    budget?: Budget,
  ): Promise<void>;
}

export type CostApportionmentOfParty = {
  readonly partyId: string;
  partyName: string;
  apportionment?: CostApportionment;
  opponents: string[];
};

export const CostApportionments = () => {
  const caseContext = useCase();
  const parties = caseContext.getPartyDescriptors();

  const submit = useCaseSubmit();
  const apportionmentCallbacks: CostApportionmentCallbacks = {
    update: function (
      partyId: string,
      apportionment: CostApportionment,
      budget?: Budget,
    ): Promise<void> {
      const party = caseContext
        .getCase()
        .parties?.find((p) => p.id === partyId);
      if (party === undefined) {
        console.warn(
          "Cannot update cost apportionment, no party exists with ID " +
            partyId,
        );
        return Promise.resolve();
      }
      return Promise.resolve(
        submit(
          {
            party: JSON.stringify({ ...party, apportionment }),
            ...(budget && { budget: JSON.stringify(budget) }),
          },
          { method: "put" },
        ),
      );
    },
  };

  const apportionments: CostApportionmentOfParty[] =
    caseContext.getCase().parties?.map((party) => ({
      partyId: party.id,
      partyName: party.name,
      apportionment: party.apportionment,
      opponents: [],
    })) || [];

  // calculate who needs apportionment v. whom
  const partiesWithMultipleOpponents: CostApportionmentOfParty[] = [];

  const getOpponents = (partyId: string) =>
    caseContext.getPartyDescriptors()[partyId]?.opponents ?? [];
  apportionments.forEach((apportionment) => {
    const opponents = new Set(getOpponents(apportionment.partyId));
    // don't forget existing apportionments
    Object.keys(apportionment.apportionment?.perParty || {}).forEach(
      (opponent) => opponents.add(opponent),
    );
    if (opponents.size > 1)
      partiesWithMultipleOpponents.push({
        ...apportionment,
        opponents: [...opponents],
      });
  });

  return (
    <CostApportionmentsComponent
      apportionments={partiesWithMultipleOpponents}
      apportionmentCallbacks={apportionmentCallbacks}
      parties={parties}
    />
  );
};

export const CostApportionmentsComponent = ({
  apportionments,
  apportionmentCallbacks,
  parties,
}: {
  apportionments: CostApportionmentOfParty[];
  apportionmentCallbacks: CostApportionmentCallbacks;
  parties: PartyDescriptors;
}) => {
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, { keyPrefix: "case" });
  const navigate = useNavigate();
  const caseContext = useCase();
  const caseId = caseContext.getCase().id;
  if (apportionments.length === 0) {
    return (
      <CenteredMessageBox
        message={t("apportionments.description", { count: 0 })}
        action={{
          label: t("apportionments.goToParties"),
          icon: <PartiesIcon />,
          onClick: () => navigate(`/cases/${caseId}/configuration/parties`),
        }}
      />
    );
  }
  return (
    <Stack spacing={1.5}>
      <GuideHeader>
        <Box>
          <Typography
            variant="h5"
            component="h1"
            sx={{ fontWeight: 700, letterSpacing: "-.01em" }}
          >
            {t("apportionments.title")}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", mt: 0.5, maxWidth: "80ch" }}
          >
            {t("apportionments.description", { count: apportionments.length })}
          </Typography>
        </Box>
      </GuideHeader>
      <Box>
        <Tabs
          tabs={apportionments.map((apportionment) => ({
            label: (
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <span>{apportionment.partyName}</span>
                {parties[apportionment.partyId]?.isClient && (
                  <TonalBadge tone={theme.palette.primary}>
                    {t("parties.clientBadge")}
                  </TonalBadge>
                )}
              </Stack>
            ),
            tabComponent: (
              <CostApportionmentsOfParty
                apportionment={apportionment}
                budget={caseContext
                  .getCase()
                  .budgets?.find(
                    ({ partyId }) => partyId === apportionment.partyId,
                  )}
                locked={
                  caseContext.getCase().results?.lockedCosts?.[
                    apportionment.partyId
                  ] ?? NOTHING_LOCKED
                }
                opponents={apportionment.opponents}
                parties={parties}
                update={(newValue, budget) =>
                  apportionmentCallbacks.update(
                    apportionment.partyId,
                    newValue,
                    budget,
                  )
                }
                key={"apportionment_" + apportionment.partyId}
              />
            ),
          }))}
        />
      </Box>
    </Stack>
  );
};
